import React, { useState } from 'react';
import { Button, Col, Form, Input, notification, Radio, Row, Typography } from 'antd';
import { Link } from 'react-router-dom';
import { RadioChangeEvent } from 'antd/es/radio';
import { TimePickerCommon } from 'common/components/DateTime/TimePickerCommon';
import { formRules, requireText } from 'common/const/common.const';
import { ERoutesPrivate } from 'common/models/routesModel';
import { ECommonFormType } from 'common/models/commonModel';
import { MapPlacePicker } from 'common/components/Map/MapPlacePicker';
import { useFormMapper } from 'common/helpers/form.helper';
import { useIsContentRestricted } from 'common/hooks/useIsContentRestricted';
import { useAppSelector } from 'app/store/store.hooks';
import { EStoreWorkSchedule, IStoreCreateData, IStoreUpdateData } from 'entities/Store/Store.models';
import { UserSelector } from 'entities/User/components/Selector/UserSelector';
import { StoreStatusSelector } from 'entities/Store/components/Selector/StoreStatusSelector';
import { EUserRole } from 'entities/User/User.models';
import { AssetSelector } from 'entities/Asset/components/Selector/AssetSelector';
import { RegionSelector } from 'entities/Region/components/Selector/RegionSelector';
import { GroupSelector } from 'entities/Region/components/Selector/GroupSelector';
interface IComponentProps {
  createStore?: (data: IStoreCreateData) => void;
  updateStore?: (data: IStoreUpdateData) => void;
  typeForm: ECommonFormType;
}

export const StoreForm: React.FC<IComponentProps> = ({ createStore, updateStore, typeForm }) => {
  const [form] = Form.useForm();
  const { storeModel, storeModelLoading, storeModelParams, storeModelError } = useAppSelector((state) => state.store);
  const [workScheduleValue, setWorkSchedule] = useState<EStoreWorkSchedule>(
    (storeModel?.schedule as EStoreWorkSchedule) || EStoreWorkSchedule.FullTime
  );
  const isCreateForm = typeForm === ECommonFormType.Add;
  const submitButtonText = isCreateForm ? 'Add store' : 'Save';
  const canManageStore = !useIsContentRestricted();
  const isFieldDisabled = storeModelLoading || !canManageStore;
  const isRequiredFranchiseeSelector = !isCreateForm && !!storeModel?.franchisee;
  const isRequiredAssetSelector = !isCreateForm && !!storeModel?.asset;

  const { fields } = useFormMapper(
    [
      'email',
      'password',
      'storeCode',
      'storeName',
      'phone',
      'franchisee',
      'openTime',
      'closeTime',
      'address',
      'asset',
      'status',
      'regionId',
      'groupId',
    ],
    storeModel,
    storeModelParams?.data,
    storeModelError
  );

  // region changed → drop any group from the previous region (can't keep a cross-region group)
  // and clear its validation error, since the required rule is re-evaluated for the new region.
  const onChangeRegion = () => {
    form.setFields([{ name: 'groupId', value: undefined, errors: [] }]);
  };

  const saveStore = (values: any) => {
    const {
      email,
      storeName,
      phone,
      password,
      openTime,
      closeTime,
      storeCode,
      franchisee,
      address,
      status,
      asset,
      regionId,
      groupId,
    } = values;

    const vals: IStoreUpdateData = {
      storeName,
      password,
      franchisee,
      asset,
      openTime,
      closeTime,
      schedule: workScheduleValue,
      phone,
      email,
      address,
      regionId: regionId ?? null,
      groupId: groupId ?? null,
    };

    if (franchisee) {
      vals.franchisee = franchisee;
    }
    if (asset) {
      vals.asset = asset;
    }

    isCreateForm ? createStore && createStore({ ...vals, storeCode } as IStoreCreateData) : notification.destroy();
    updateStore && updateStore({ ...vals, status });
  };

  const changeWorkSchedule = (e: RadioChangeEvent) => {
    setWorkSchedule(e.target.value);
  };

  const openNotification = () => {
    if (!isCreateForm) {
      notification.destroy();
      notification.warning({
        message: `Warning!`,
        description: "You are about to reset this store's current password",
        placement: 'topRight',
        duration: null,
      });
    }
  };

  return (
    <>
      <span className="basic__text_title" title="General info">
        General info
      </span>
      <Form className="mt-5" form={form} onFinish={saveStore} fields={fields} layout="vertical" autoComplete="off">
        <Row gutter={[28, 10]}>
          <Col span={12}>
            <Form.Item
              rules={[formRules.required, { type: 'email', message: 'Please enter correct email' }]}
              name="email"
              label="Store email"
            >
              <Input type="email" autoComplete="off" name="email" placeholder="Email" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: isCreateForm,
                  message: requireText,
                },
                {
                  min: 8,
                  message: 'Password must be longer than or equal 8 characters',
                },
                {
                  max: 16,
                  message: 'Password must be shorter than or equal 16 characters',
                },
              ]}
              name="password"
              label="Store password"
            >
              <Input.Password
                type="password"
                autoComplete="new-password"
                placeholder="Password"
                disabled={isFieldDisabled}
                onFocus={openNotification}
              />
            </Form.Item>
          </Col>
          {!isCreateForm && (
            <Col span={12}>
              <Form.Item rules={[formRules.required]} name="status" label="Store status">
                <StoreStatusSelector disabled={isFieldDisabled} />
              </Form.Item>
            </Col>
          )}
          {isCreateForm && (
            <Col span={12}>
              <Form.Item
                rules={[
                  formRules.required,
                  {
                    min: 3,
                    max: 4,
                    message: 'Store code should be equal to 3 or 4 digits',
                  },
                ]}
                name="storeCode"
                label="Store code"
              >
                <Input name="code" type="text" placeholder="Store code" disabled={isFieldDisabled} />
              </Form.Item>
            </Col>
          )}
          <Col span={12}>
            <Form.Item
              rules={[
                formRules.required,
                {
                  min: 2,
                  message: 'Store name must be longer than or equal to 2 characters',
                },
                {
                  max: 20,
                  message: 'Store name must be shorter than or equal to 20 characters',
                },
              ]}
              name="storeName"
              label="Store name"
            >
              <Input name="storeName" type="text" placeholder="Store name" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>

          <Col span={12}>
            <Form.Item rules={[formRules.required, formRules.correctPhone]} name="phone" label="Contact phone">
              <Input name="phone" type="tel" placeholder="+63 9XX XXX XXXX" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>
        </Row>
        <Col span={24}>
          <Form.Item rules={[formRules.required]} name="address" label="Please select store location">
            <MapPlacePicker disabled={isFieldDisabled} />
          </Form.Item>
        </Col>

        <Row className="mt-8 mb-5" align="middle">
          <span className="basic__text_title" title="Management">
            Management
          </span>
        </Row>
        <Row gutter={[28, 10]}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: isRequiredFranchiseeSelector,
                  message: requireText,
                },
              ]}
              name="franchisee"
              label="Franchisee"
            >
              <UserSelector
                placeholder="Enter franchisee name"
                role={EUserRole.Franchisee}
                disabled={isFieldDisabled}
              />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item label="Region" name="regionId">
              <RegionSelector onChange={onChangeRegion} placeholder="Select region" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>
          <Col span={12}>
            <Form.Item noStyle shouldUpdate={(prev, cur) => prev.regionId !== cur.regionId}>
              {({ getFieldValue }) => {
                const regionId = getFieldValue('regionId');

                return (
                  <Form.Item
                    label="Group"
                    name="groupId"
                    rules={
                      regionId ? [{ required: true, message: 'Group is required when a region is selected.' }] : []
                    }
                  >
                    <GroupSelector
                      regionId={regionId}
                      placeholder={regionId ? 'Select group' : 'Select a region first'}
                      disabled={isFieldDisabled}
                    />
                  </Form.Item>
                );
              }}
            </Form.Item>
          </Col>
        </Row>
        <Col span={24} className="mt-5">
          <span className="basic__text_title" title="Menu asset">
            Menu asset
          </span>
        </Col>
        <Row gutter={[28, 0]}>
          <Col span={12}>
            <Form.Item
              rules={[
                {
                  required: isRequiredAssetSelector,
                  message: requireText,
                },
              ]}
              label="Please select asset"
              name="asset"
            >
              <AssetSelector placeholder="Enter asset name" disabled={isFieldDisabled} />
            </Form.Item>
          </Col>
          <Col span={12} className="mt-9">
            <Link
              to={ERoutesPrivate.AssetCreate}
              target="_blank"
              onClick={(e) => {
                if (!canManageStore) {
                  e.preventDefault();
                }
              }}
            >
              <Typography.Text className="fs-xs color-blue" disabled={!canManageStore}>
                Create custom asset
              </Typography.Text>
            </Link>
          </Col>
        </Row>
        <Col span={24} className="mt-5 mb-5">
          <span className="basic__text_title" title="Working hours">
            Working hours
          </span>
        </Col>
        <Row gutter={[28, 12]}>
          <Col span={24}>
            <Radio.Group
              onChange={changeWorkSchedule}
              disabled={isFieldDisabled}
              defaultValue={EStoreWorkSchedule.FullTime}
              value={workScheduleValue}
            >
              <Radio value={EStoreWorkSchedule.FullTime}>{EStoreWorkSchedule.FullTime}</Radio>
              <Radio value={EStoreWorkSchedule.MonSat}>{EStoreWorkSchedule.MonSat}</Radio>
              <Radio value={EStoreWorkSchedule.MonSun}>{EStoreWorkSchedule.MonSun}</Radio>
            </Radio.Group>
          </Col>
          {workScheduleValue !== EStoreWorkSchedule.FullTime && (
            <>
              <Col span={6}>
                <Form.Item rules={[formRules.required]} name="openTime" label="Opens at">
                  <TimePickerCommon disabled={isFieldDisabled} />
                </Form.Item>
              </Col>
              <Col span={6}>
                <Form.Item rules={[formRules.required]} name="closeTime" label="Closes at">
                  <TimePickerCommon disabled={isFieldDisabled} />
                </Form.Item>
              </Col>
            </>
          )}
        </Row>
        <Col span={10} className="mt-4">
          <Row gutter={[28, 0]}>
            <Col span={12}>
              <Form.Item className="mb-0">
                <Button
                  htmlType="submit"
                  type="primary"
                  block
                  disabled={storeModelLoading || !canManageStore}
                  loading={storeModelLoading}
                  title={submitButtonText}
                >
                  {submitButtonText}
                </Button>
              </Form.Item>
            </Col>
            <Col span={12}>
              <Link to={ERoutesPrivate.Stores}>
                <Button
                  block
                  disabled={storeModelLoading}
                  loading={storeModelLoading}
                  title="Cancel"
                  onClick={notification.destroy}
                >
                  Cancel
                </Button>
              </Link>
            </Col>
          </Row>
        </Col>
      </Form>
    </>
  );
};
